// pdfmake
import { Table, TableCell } from 'pdfmake/interfaces';

// i18n
import { TFunction } from 'i18next';

// types
import { FileToZip, IfrTable, IfrTables, PdfTable, Project } from '../@types';

// utils
import { formatDate } from './dates';
import { jsonToCsv } from './files';
import { formatNumber } from './format';

// constants
export const cellWidths = {
	lg: 350,
	md: 140,
	sm: 120,
};

// types
export interface SplitTableColumn {
	field: string;
	headerName?: string;
}

export interface SplitTableRow {
	id: string | number;
	[key: string]: number | string;
}

export interface SplitTable {
	columns: SplitTableColumn[];
	rows: SplitTableRow[];
	title?: string;
	isPartTable?: boolean;
}

export interface SplitWideTableParams {
	table: {
		columns: SplitTableColumn[];
		rows: SplitTableRow[];
	};
	title?: string;
	maxCols?: number;
	hasFirstColInSplit?: boolean;
}

export const splitWideTable = ({
	table,
	title,
	maxCols = 12,
	hasFirstColInSplit = true,
}: SplitWideTableParams): SplitTable[] => {
	if (!table.columns || table.columns.length === 0) {
		return [{ ...table, title }];
	}

	const columnCount = table.columns.length;

	// If table fits within max columns, return as-is
	if (columnCount <= maxCols) {
		return [{ ...table, title }];
	}

	// Calculate splits upfront
	const columnsPerSplit = hasFirstColInSplit ? maxCols - 1 : maxCols;
	const startingColumn = hasFirstColInSplit ? 1 : 0;
	const numSplits = Math.ceil((columnCount - startingColumn) / columnsPerSplit);

	// Create each split table
	return Array.from({ length: numSplits }, (_, splitIndex) => {
		const startCol = startingColumn + splitIndex * columnsPerSplit;
		const endCol = Math.min(startCol + columnsPerSplit, columnCount);

		// Create column indices for this split
		const columnIndices = hasFirstColInSplit
			? [0, ...Array.from({ length: endCol - startCol }, (_, i) => startCol + i)]
			: Array.from({ length: endCol - startCol }, (_, i) => startCol + i);

		// Create new columns with selected columns
		const columnsSplit = columnIndices.map((colIndex) => table.columns[colIndex]);

		// Create new table rows with selected columns
		const rowsSplit = table.rows.map((row) => {
			const newRow: SplitTableRow = { id: row.id };
			columnIndices.forEach((colIndex) => {
				const field = table.columns[colIndex].field;
				newRow[field] = row[field];
			});
			return newRow;
		});

		return {
			columns: columnsSplit,
			rows: rowsSplit,
			title: splitIndex === 0 ? title : `${title} (Part ${splitIndex + 1})`,
			isPartTable: splitIndex > 0,
		};
	});
};

export interface SanitizeIfrTableRowsParams {
	key: string;
	rows: IfrTable['rows'];
}

export const sanitizeIfrTableRowsForExport = ({ rows = [], key }: SanitizeIfrTableRowsParams) => {
	if (['cashIn', 'cashOutCategories', 'cashOutComponents'].includes(key)) {
		// remove first and second rows
		rows = rows.filter((_, i) => ![0, 1].includes(i));
	} else if (
		[
			'closingBalances',
			'forecast',
			'openingBalances',
			'reconciliation',
			'reimbursementCategories',
			'reimbursementComponents',
		].includes(key)
	) {
		// remove first row
		rows = rows.filter((_, i) => ![0].includes(i));
	}

	return rows;
};

export interface IfrTableTitlesParams {
	key: string;
	t: TFunction;
}

export const getIfrTableTitle = ({ key, t }: IfrTableTitlesParams) => {
	const ifrTableTitles: Record<string, string> = {
		cashIn: t('tableSheets.cashIn.title'),
		cashOutCategories: t('tableSheets.cashOutCategories.title'),
		cashOutComponents: t('tableSheets.cashOutComponents.title'),
		closingBalances: t('closingCashBalances'),
		forecast: t('forecast'),
		openingBalances: t('openingCashBalances'),
		reconciliation: t('reconciliation'),
		reimbursementCategories: t('tableSheets.reimbursementCategories.title'),
		reimbursementComponents: t('tableSheets.reimbursementComponents.title'),
	};
	return ifrTableTitles[key] || key;
};

export interface IfrTablesToPdfTablesParams {
	ifrTables: IfrTables;
	t: TFunction;
}

export const ifrTablesToPdfTables = ({ ifrTables, t }: IfrTablesToPdfTablesParams): PdfTable[] => {
	const tables: PdfTable[] = [];

	Object.entries(ifrTables).forEach(([key, ifrTable]: [string, IfrTable]) => {
		// Convert rows to table format for PDF using TableCell objects
		const body: TableCell[][] = [];

		// Add header row if columns exist
		if (ifrTable.columns && ifrTable.columns.length > 0) {
			const headerRow: TableCell[] = ifrTable.columns.map((col) => ({
				text: col.headerName || col.field || '',
			}));
			body.push(headerRow);
		}

		// Add data rows
		if (ifrTable.rows && ifrTable.rows.length > 0) {
			// Sanitize rows
			const rows = sanitizeIfrTableRowsForExport({ rows: ifrTable.rows, key });

			rows.forEach((row) => {
				const dataRow: TableCell[] = ifrTable.columns.map((col) => {
					const value = row[col.field];
					let text: string;

					// Format the value appropriately
					if (value === null || value === undefined) {
						text = '';
					} else if (typeof value === 'number') {
						text = formatNumber(value);
					} else {
						text = String(value);
					}

					const cell: TableCell = {
						text,
					};

					return cell;
				});
				body.push(dataRow);
			});
		}

		// Create pdfmake table
		const table: Table = {
			body,
			widths: ifrTable.columns ? ifrTable.columns.map(() => 'auto' as const) : [],
		};

		// Translate title if possible
		const title = getIfrTableTitle({ key, t });

		// Create PdfTable object
		const pdfTable: PdfTable = {
			hasFirstColInSplit: true,
			header: { title },
			table,
		};

		tables.push(pdfTable);
	});

	return tables;
};

export interface IfrTablesToCsvFilesParams {
	ifrTables: IfrTables;
	project: Project;
}

export const ifrTablesToCsvFiles = ({
	ifrTables,
	project,
}: IfrTablesToCsvFilesParams): FileToZip[] => {
	const csvFiles: FileToZip[] = [];

	Object.entries(ifrTables).forEach(([key, table]: [string, IfrTable]) => {
		// Convert table to array format for CSV
		const csvData: Record<string, string | number>[] = [];

		if (table.rows && table.rows.length > 0) {
			// Sanitize rows
			const rows = sanitizeIfrTableRowsForExport({ rows: table.rows, key });

			// Create objects for CSV conversion
			rows.forEach((row) => {
				const csvRow: Record<string, string | number> = {};
				table.columns.forEach((col) => {
					const headerName = col.headerName || col.field || '';
					const value = row[col.field];

					// Format the value appropriately
					if (value === null || value === undefined) {
						csvRow[headerName] = '';
					} else if (typeof value === 'number') {
						csvRow[headerName] = value;
					} else {
						csvRow[headerName] = String(value);
					}
				});
				csvData.push(csvRow);
			});
		}

		if (csvData.length > 0) {
			// Convert to CSV string
			const csvContent = jsonToCsv({ array: csvData });

			// Create filename from table key
			const tableName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
			const dateString = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
			const filename = `${project.idWbg}-ifr-${tableName}-${dateString}.csv`;

			csvFiles.push({
				filename,
				content: csvContent,
			});
		}
	});

	return csvFiles;
};
